import { useMemo } from "react";
import moment from "moment-timezone";
import { useCompany } from "../../../../../utils/hooks/company";
import { useGroups } from "../../../../Settings/General/OrganisationLevelGroups/context";
import { useAddedPayCode } from "../../../../TimeSheet/components/TimeSheetTable/hooks/useAddedPayCode";
import { getPrimaryActiveResource } from "../../../../../utils/helpers/user";
import { combineDateAndTime, renderDurationAsFormat } from "../../../../../utils/helpers/date";
import { getAutomaticBreaks } from "../../../../TraxxionSchedule/Component/helpers/form/sections";

/** Defaults a new shift inherits from the user, their calculation group and the company settings. */
export const useDefaultSection = ({ user, date, data }) => {
    const company = useCompany();
    const { allocatedGroups } = useGroups();
    const defaultPayCode = useAddedPayCode(user);

    const primaryJob = useMemo(() => getPrimaryActiveResource(user?.jobs || []), [user?.jobs]);
    const primaryLocation = useMemo(() => getPrimaryActiveResource(user?.locations || []), [user?.locations]);

    const levels = useMemo(() => {
        return allocatedGroups?.reduce((total, group) => {
            const level = group?.level.replace("_", "");
            if (user?.[level]) {
                return { ...total, [level]: user[level] };
            } else if (data?.[level]) {
                return { ...total, [level]: data[level] };
            }
            return total;
        }, {});
    }, [allocatedGroups, user, data]);

    return useMemo(() => {
        const calcGroup = user?.calculationGroup;
        const scheduleSettings = calcGroup?.schedule || company?.settings?.schedule;
        const shiftStart = scheduleSettings?.shiftStart
            ? moment.parseZone(scheduleSettings.shiftStart)
            : moment().startOf("day").hour(9);
        const shiftEnd = scheduleSettings?.shiftEnd
            ? moment.parseZone(scheduleSettings.shiftEnd)
            : moment().startOf("day").hour(17);

        const start = combineDateAndTime(moment(date), shiftStart);
        let end = combineDateAndTime(moment(date), shiftEnd);
        if (moment(end).isBefore(moment(start), "seconds")) {
            end = moment(end).add(1, "day");
        }
        const duration = moment(end).diff(moment(start), "seconds");

        const defaultSection = {
            type: "regular",
            payCode: defaultPayCode,
            location: primaryLocation,
            job: primaryJob,
            duration: renderDurationAsFormat(duration, "HH:mm"),
            start,
            end,
            ...levels,
        };

        const breaks = calcGroup?.breaks?.automatic?.status
            ? (getAutomaticBreaks(calcGroup.breaks, [defaultSection], defaultSection) || [])
            : [];

        return { defaultSection, breaks };
    }, [user?.calculationGroup, company?.settings?.schedule, date, defaultPayCode, primaryLocation, primaryJob, levels]);
};
